import * as React from "react";
import { Box, Button, Container, Grid, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import WorkspacesRoundedIcon from "@mui/icons-material/WorkspacesRounded";
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import { useAuth } from "../context/AuthContext";
import ButtonNav from "../components/General/ButtonNav";
import TaskCard from "../components/Workspace/TaskCard";
import { getWorkspaceTasks } from "../services/WorkspaceService";
import { NAVIGATION } from "../config/navigation/navigation.config";
import { NavIcon } from "../config/navigation/navigation.icons";

// arma lista de items para el home (incluye children)
function buildHomeItems(nav) {
  const out = [];

  for (const item of nav) {
    if (item.home?.show) out.push(item);

    if (Array.isArray(item.children)) {
      for (const child of item.children) {
        if (child.home?.show) out.push(child);
      }
    }
  }

  return out;
}

export default function Home() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const canSeeWorkspace = hasPermission("area de trabajo");

  const [recentTasks, setRecentTasks] = React.useState([]);
  const [loadingTasks, setLoadingTasks] = React.useState(canSeeWorkspace);

  const homeItems = React.useMemo(() => {
    return buildHomeItems(NAVIGATION).filter((item) => {
      if (!item.permission) return true;
      return hasPermission(item.permission);
    });
  }, [hasPermission]);

  React.useEffect(() => {
    if (!canSeeWorkspace) return;

    let active = true;

    async function fetchRecentTasks() {
      setLoadingTasks(true);
      try {
        const res = await getWorkspaceTasks({ page: 1, perPage: 3, sort: "-created_at" });
        if (active) setRecentTasks(res.data?.data || []);
      } catch (error) {
        console.error("[Home] Error al cargar tareas del área de trabajo:", error);
      } finally {
        if (active) setLoadingTasks(false);
      }
    }

    fetchRecentTasks();
    return () => {
      active = false;
    };
  }, [canSeeWorkspace]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* <Typography sx={{ fontSize: 34, fontWeight: 950 }}>
        Bienvenido al Sistema
      </Typography> */}

      {canSeeWorkspace && (loadingTasks || recentTasks.length > 0) && (
        <Box sx={{ mt: 2, mb: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 3,
                  display: "grid",
                  placeItems: "center",
                  background: isDark ? "rgba(25,118,210,0.12)" : "rgba(255,255,255,0.86)",
                  border: "1px solid rgba(25,118,210,0.22)",
                  boxShadow: "0 8px 22px rgba(0,0,0,0.08)",
                  color: "primary.main",
                  "& svg": { fontSize: 20 },
                }}
              >
                <WorkspacesRoundedIcon />
              </Box>
              <Typography sx={{ fontSize: 18, fontWeight: 900, lineHeight: 1.15 }}>
                Área de Trabajo
              </Typography>
            </Box>

            <Button
              onClick={() => navigate("/workspace")}
              endIcon={<ArrowForwardRoundedIcon />}
              size="small"
              sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
            >
              Ver todas
            </Button>
          </Box>

          <Grid container spacing={2}>
            {loadingTasks
              ? [...Array(3)].map((_, i) => (
                  <Grid key={i} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Box
                      sx={{
                        height: 170,
                        borderRadius: 4,
                        bgcolor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                      }}
                    />
                  </Grid>
                ))
              : recentTasks.map((task) => (
                  <Grid key={task.id} size={{ xs: 12, sm: 6, md: 4 }}>
                    <TaskCard task={task} />
                  </Grid>
                ))}
          </Grid>
        </Box>
      )}

      {canSeeWorkspace && (loadingTasks || recentTasks.length > 0) && (
        <Box
          sx={{
            height: 2,
            borderRadius: 999,
            mb: 3,
            background: isDark
              ? "linear-gradient(90deg, transparent, rgba(25,118,210,0.5), transparent)"
              : "linear-gradient(90deg, transparent, rgba(25,118,210,0.35), transparent)",
          }}
        />
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mt: canSeeWorkspace ? 0 : 2, mb: 2 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 3,
            display: "grid",
            placeItems: "center",
            background: isDark ? "rgba(25,118,210,0.12)" : "rgba(255,255,255,0.86)",
            border: "1px solid rgba(25,118,210,0.22)",
            boxShadow: "0 8px 22px rgba(0,0,0,0.08)",
            color: "primary.main",
            "& svg": { fontSize: 20 },
          }}
        >
          <AppsRoundedIcon />
        </Box>
        <Typography sx={{ fontSize: 18, fontWeight: 900, lineHeight: 1.15 }}>
          Módulos
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {homeItems.map((item) => (
          <Grid key={item.segment} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <ButtonNav
              title={item.title}
              subtitle={item.home?.description}
              icon={<NavIcon iconKey={item.iconKey} />}
              color={item.home?.color}
              to={`/${item.segment}`}
            />
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
